import clientTypes from "constants/clientTypes";
import {
  AdminResetUserPasswordCommand,
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import axios from "axios";
import get from "utils/get";
import parseJWTPayload from "./utils/parseJWTPayload";

const COGNITO_EXTERNAL_REGION = process.env.REACT_APP_COGNITO_EXTERNAL_REGION;
const COGNITO_EXTERNAL_CLIENT_ID =
  process.env.REACT_APP_COGNITO_EXTERNAL_CLIENT_ID;
const REACT_APP_COGNITO_EXTERNAL_POOL_ID =
  process.env.REACT_APP_COGNITO_EXTERNAL_POOL_ID;

const APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const client = new CognitoIdentityProviderClient({
  region: COGNITO_EXTERNAL_REGION,
});

export default class Auth {
  accessToken;
  idToken;
  expiresAt;

  constructor() {
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getIdToken = this.getIdToken.bind(this);
    this.renewSession = this.renewSession.bind(this);
    this.resetPassword = this.resetPassword.bind(this);
  }

  async login({ email, password }) {
    const command = new InitiateAuthCommand({
      AuthFlow: "USER_PASSWORD_AUTH",
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
      ClientId: COGNITO_EXTERNAL_CLIENT_ID,
    });

    let result;

    try {
      result = await client.send(command);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("error", error);

      result = {
        error: {
          message: error.message,
          type: error.__type,
        },
      };
    }

    return result;
  }

  async checkUserIsActive({ email }) {}

  async setNewPassword({
    email,
    newPassword,
    firstName,
    lastName,
    mobileNumber,
    cognitoResponse,
  }) {
    if (firstName && lastName && mobileNumber) {
      // need to check if we already have each of these attributes
    }

    const updateCommand = {
      ClientId: COGNITO_EXTERNAL_CLIENT_ID,
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      Session: cognitoResponse.Session,
      ChallengeResponses: {
        USERNAME: email,
        NEW_PASSWORD: newPassword,
        // "userAttributes.given_name": firstName,
        // "userAttributes.family_name": lastName,
        // "userAttributes.phone_number": mobileNumber,
      },
    };

    const requiredAttributes =
      JSON.parse(cognitoResponse?.ChallengeParameters?.requiredAttributes) ||
      [];

    // Add the required attributes to the updateCommand (if needed)
    for (const attribute of requiredAttributes) {
      switch (attribute) {
        case "given_name":
          updateCommand.ChallengeResponses[`userAttributes.${attribute}`] =
            firstName;
          break;
        case "family_name":
          updateCommand.ChallengeResponses[`userAttributes.${attribute}`] =
            lastName;
          break;
        case "phone_number":
          updateCommand.ChallengeResponses[`userAttributes.${attribute}`] =
            mobileNumber;
          break;

        default:
          break;
      }
    }

    let result;

    try {
      const challengeResponse = new RespondToAuthChallengeCommand(
        updateCommand
      );
      result = await client.send(challengeResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("error", error);
    }
    return result;
  }

  async resetPassword({ email }) {
    let response = {};

    const input = {
      UserPoolId: REACT_APP_COGNITO_EXTERNAL_POOL_ID,
      Username: email,
    };

    const command = new AdminResetUserPasswordCommand(input);

    try {
      response = await client.send(command);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ERROR: adminResetUserPassword");
      // eslint-disable-next-line no-console
      console.error(error);
    }

    return response;
  }

  async confirmMFA({
    email,
    code,
    codeDeliveryDestination,
    challengeName,
    cognitoSession,
  }) {
    const challengeResponse = new RespondToAuthChallengeCommand({
      ClientId: COGNITO_EXTERNAL_CLIENT_ID,
      ChallengeName: challengeName,
      Session: cognitoSession,
      ChallengeResponses: {
        USERNAME: email,
        SMS_MFA_CODE: code,
      },
    });

    let challengeResult;
    let user = {};
    try {
      challengeResult = await client.send(challengeResponse);

      user = await this.handleResult(challengeResult);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("error", error);

      challengeResult = {
        error: {
          message: error.message,
          type: error.__type,
        },
      };
    }

    return user;
  }

  async handleResult(challengeResult) {
    let user = {};

    if (
      challengeResult?.$metadata?.httpStatusCode === 200 &&
      challengeResult?.AuthenticationResult
    ) {
      const expiresIn = get(challengeResult, "AuthenticationResult.ExpiresIn");
      const accessToken = get(
        challengeResult,
        "AuthenticationResult.AccessToken"
      ); // Contains info for Cognito
      const idToken = get(challengeResult, "AuthenticationResult.IdToken"); // Contains user info - this looks like the main one we need
      const refreshToken = get(
        challengeResult,
        "AuthenticationResult.RefreshToken"
      ); // Not sure if we need this yet

      const token = {
        expiresIn,
        accessToken,
        idToken,
        refreshToken,
        tokenType: "Bearer",
      };

      user = await this.handleAuthentication(token);
    }
    return user;
  }

  async handleAuthentication(token) {
    const idToken = token.idToken;

    let user = {};

    // Pass the token to the BE for user/roles
    const response = await axios
      .post(`${APP_API_BASE_URL}/sso/login`, {
        idToken: idToken,
      })
      .catch(function (...args) {
        // this.handleLoginError();
        // eslint-disable-next-line no-console
        console.error("Error", args);
        return user;
      });

    user = response.data;

    if (!user || user?.active !== 1) {
      localStorage.clear();
      window.location.replace("/login?error=wms-denied");
      return false;
    } else {
      const firstName = get(user, "firstName", "");
      const lastName = get(user, "lastName", "");
      const name = `${firstName} ${lastName}`;

      token.firstName = firstName;
      token.lastName = lastName;
      token.name = name;
      token.idTokenPayload = {
        ...parseJWTPayload(idToken),
        name: user.email,
      };

      this.setSession(token);

      if (user.warehouses) {
        const warehouses = user.warehouses;
        localStorage.setItem("warehouses", JSON.stringify(warehouses));
        const activeWarehouseId = get(warehouses, "[0].warehouseId");
        const activeWarehouseOperatorId = get(
          warehouses,
          "[0].warehouseOperatorId"
        );
        localStorage.setItem(
          "activeWarehouseId",
          JSON.stringify(activeWarehouseId)
        );
        localStorage.setItem(
          "activeWarehouseOperatorId",
          JSON.stringify(activeWarehouseOperatorId)
        );
      }

      if (user.permissions) {
        const permissions = user.permissions.map((p) => p.permissionName);
        localStorage.setItem("permissions", JSON.stringify(permissions));
      }

      // must be last due to redirects
      if (user.accountTypes) {
        const accountTypes = user.accountTypes;
        const hasWMSType = accountTypes.some(
          (accountType) => accountType.type === "wms"
        );

        if (!hasWMSType) {
          this.logout();
          return {
            error:
              "You do not have permission to access WMS.  Please contact IT Support.",
          };
        }

        localStorage.setItem("accountTypes", JSON.stringify(accountTypes));
      }
    }
    return user;
  }

  getAccessToken() {
    let token = localStorage.getItem("token");
    if (token === null) {
      return {};
    }
    try {
      token = JSON.parse(token);
      return token;
    } catch (e) {
      return {};
    }
  }

  getAccessTokenPayload() {
    let { accessToken } = this.getAccessToken();
    if (!accessToken) {
      return {};
    }
    return parseJWTPayload(accessToken);
  }

  getIdToken() {
    const { idToken } = this.getAccessToken();
    return idToken;
  }

  setSession(token) {
    localStorage.setItem("token", JSON.stringify(token));
  }

  renewSession() {
    // TODO: Investigate refreshToken
  }

  signup({ email, password }) {}

  logout() {
    localStorage.clear();
  }

  isAuthenticated() {
    if (window.location.hash) {
      const fragmentMap = this.decodeHash(window.location.hash);
      const accessToken = fragmentMap.access_token;
      const idToken = fragmentMap.id_token;

      let jwt = this.parseJwt(idToken);

      jwt.accessToken = accessToken;
      jwt.idToken = idToken;

      this.processJwt(jwt);
    } else if (new URLSearchParams(window.location.search).has("code")) {
      const code = new URLSearchParams(window.location.search).get("code");

      // Exchange the code for tokens
      axios
        .post(`${APP_API_BASE_URL}/sso/exchange-code`, {
          code,
          redirectUri: `${window.location.origin}/login-internal`,
        })
        .then((data) => {
          const idToken = get(data?.data, "id_token");
          const accessToken = get(data?.data, "access_token");
          const refreshToken = get(data?.data, "refresh_token");

          let jwt = this.parseJwt(idToken);

          jwt.accessToken = accessToken;
          jwt.idToken = idToken;
          jwt.refreshToken = refreshToken;

          this.processJwt(jwt);
        })
        .catch(function (...args) {
          // eslint-disable-next-line no-console
          console.error("Error", args);
          // this.handleLoginError();
        });
    }

    const token = this.getAccessTokenPayload();
    const { exp } = token;

    if (!exp) {
      return false;
    }
    const now = Math.floor(Date.now() / 1000);

    return exp > now;
  }

  processJwt(jwt) {
    // Pass the token to the BE for user/roles
    axios
      .post(`${APP_API_BASE_URL}/sso/login`, {
        idToken: jwt.idToken,
      })
      .then(function (response) {
        const data = response.data;
        const firstName = get(data, "firstName", "");
        const lastName = get(data, "lastName", "");
        const name = `${firstName} ${lastName}`;

        jwt.firstName = firstName;
        jwt.lastName = lastName;
        jwt.name = name;
        jwt.permissions = [];

        if (!data || data?.active !== 1) {
          localStorage.clear();
          window.location.replace("/login?error=account-inactive");
          return false;
        }

        if (data.warehouses) {
          const warehouses = data.warehouses;
          localStorage.setItem("warehouses", JSON.stringify(warehouses));
          const activeWarehouseId = get(warehouses, "[0].warehouseId");
          const activeWarehouseOperatorId = get(
            warehouses,
            "[0].warehouseOperatorId"
          );
          localStorage.setItem(
            "activeWarehouseId",
            JSON.stringify(activeWarehouseId)
          );
          localStorage.setItem(
            "activeWarehouseOperatorId",
            JSON.stringify(activeWarehouseOperatorId)
          );
        }

        if (data.permissions) {
          const permissions = data.permissions.map((p) => p.permissionName);
          localStorage.setItem("permissions", JSON.stringify(permissions));

          jwt.permissions = data.permissions.map(
            (permission) => permission.permissionName
          );
        }

        localStorage.setItem("token", JSON.stringify(jwt));

        // must be last due to redirects
        if (data.accountTypes) {
          const accountTypes = data.accountTypes;

          const hasWMSType = accountTypes.some(
            (accountType) => accountType.type === "wms"
          );

          if (!hasWMSType) {
            localStorage.clear();
            window.location.replace("/login?error=wms-denied");
            return false;
          }

          localStorage.setItem("accountTypes", JSON.stringify(accountTypes));

          // Determine the next route based on account types
          const hasParcelsType = accountTypes.some(
            (accountType) => accountType.type === "parcels"
          );
          const hasFulfilmentType = accountTypes.some(
            (accountType) => accountType.type === "fulfilment"
          );

          if (hasParcelsType && hasFulfilmentType) {
            window.location.replace("/select-account-type");
          } else if (hasParcelsType) {
            localStorage.setItem(
              "clientType",
              JSON.stringify(clientTypes.PARCELS)
            );

            window.location.replace("/customers/manage");
          } else if (hasFulfilmentType) {
            localStorage.setItem(
              "clientType",
              JSON.stringify(clientTypes.FULFIL)
            );
            window.location.replace("/fulfil-customers/manage");
          } else {
            // Handle other cases if needed
            window.location.replace("/403");
          }
        }
      })
      .catch(function (...args) {
        // this.handleLoginError();
        // eslint-disable-next-line no-console
        console.error("Error", args);
      });
  }

  decodeHash = (hashString) => {
    if (!hashString) {
      return {};
    } else {
      return hashString.split("&").reduce((params, param) => {
        let [key, value] = param.split("=");
        key = key.replace("#", "");
        params[key] = decodeURIComponent(("" + value).replace(/\+/g, " "));
        return params;
      }, {});
    }
  };

  parseJwt = (token) => {
    let base64Url = token.split(".")[1];
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    let jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  };
}
